import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ApplicationStatus,
  IApplication,
  IApplicationSocketResponse,
  IJobOffer,
} from '../../../../../assets/types/dtoTypes';
import { JobsDetailsService } from './jobs-details.service';
import { ImageService } from '../../../../shared/services/image.service';
import { ShowPopupService } from '../../../../shared/services/show-popup.service';
import { CompanyDashboardPageService } from '../company-dashboard-page/company-dashboard-page.service';
import { NotificationService } from '../../../../shared/services/notifications.service';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {BIPPERZ_BREAKPOINTS, BreakpointsService} from "../../../../shared/services/breakpoint.service";
import {ExternalHeaderComponent} from "../../../../shared/external-header/external-header.component";
import {JobComponent} from "../jobs-page/job/job.component";
import {FooterComponent} from "../../../../shared/footer/footer.component";
import {NgClass, NgIf} from "@angular/common";
import {LoadMaskComponent} from "../../../../shared/load-mask/load-mask.component";
import {ApplicationsTableComponent} from "./applications-table/applications-table.component";

@Component({
  selector: 'app-jobs-details',
  templateUrl: './jobs-details.component.html',
  styleUrl: './jobs-details.component.scss',
  imports: [
    ExternalHeaderComponent,
    JobComponent,
    TranslateModule,
    FooterComponent,
    LoadMaskComponent,
    NgIf,
    ApplicationsTableComponent,
    NgClass
  ],
  standalone: true
})
export class JobsDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private companyService = inject(CompanyDashboardPageService);
  private jobDetailsService = inject(JobsDetailsService);
  private breakpointsService = inject(BreakpointsService);
  private showPopupService = inject(ShowPopupService);
  private notificationService = inject(NotificationService);
  private imageService = inject(ImageService);
  private translate = inject(TranslateService);


  job?: IJobOffer;

  isMobile = false;

  description: string;

  activeTab: string = 'overview';

  applications: IApplication[] = [];

  shortlist: IApplication[] = [];

  interviews: IApplication[] = [];

  decisions: IApplication[] = [];

  rejected: IApplication[] = [];

  offered: IApplication[] = [];

  hired: IApplication[] = [];

  isLoading = true;

  constructor() {

    this.breakpointsService.observe( [ BIPPERZ_BREAKPOINTS.s, BIPPERZ_BREAKPOINTS.m_down ] ).subscribe( ( result ) => {
      this.isMobile = result.s || result.m_down;
    } );

    this.notificationService.onApplicationUpdated().subscribe( ( updatedApp: IApplicationSocketResponse ) => {
      if ( !updatedApp ) return;


      const app = this.getAllApplications().find( ( application ) => application.id === updatedApp.applicationId );
      if ( !app ) return;


      this.removeFromAllLists( app.id );
      app.status = updatedApp.newStatus.toUpperCase() as ApplicationStatus;
      this.addToStatusList( app, updatedApp.newStatus.toUpperCase() );

      this.cdr.detectChanges();


      this.cdr.detectChanges();
    } );

  }


  private addToStatusList( app: IApplication, status: string ) {
    const statusUpper = status.toUpperCase();
    const list = this.getListByStatus( statusUpper );
    list.push( app );
  }

  private getListByStatus( status: string ): IApplication[] {
    switch ( status ) {
      case 'PENDING': return this.applications;
      case 'SHORTLISTED': return this.shortlist;
      case 'INVITED_TO_INTERVIEW': return this.interviews;
      case 'INTERVIEWED': return this.decisions;
      case 'OFFERED': return this.offered;
      case 'REJECTED': return this.rejected;
      case 'ACCEPTED': return this.hired;
      default: return [];
    }
  }


  ngOnInit(): void {
    const jobId = this.route.snapshot.paramMap.get( 'id' );
    if ( !jobId ) return;

    this.jobDetailsService.getJobOffer( jobId ).subscribe( job => {
      this.job = job;

      if ( this.job ) {
        this.imageService.appendSasTokenToCompanyImage( this.job.companyLogoUrl, this.job.companyId, true ).subscribe( url => {
          this.job.companyLogoUrl = url;

          this.jobDetailsService.getAllApplications( jobId ).subscribe( applications => {
            const imageUpdateTasks = applications.map( app =>
              this.imageService.appendSasTokenToProfileImageUrl( app.applicantProfileImageUrl, app.userId )
                .toPromise()
                .then( ( applicantUrl ) => {
                  this.applications = applications;
                  app.applicantProfileImageUrl = applicantUrl;
                  return app;
                } ),
            );

            Promise.all( imageUpdateTasks ).then( () => {

              this.companyService.getCompanyData( this.job.companyId ).subscribe( {
                next: ( data ) => {
                  this.description = data.description;
                },
              } );

              this.isLoading = false;
            } );
          } );
        } );

      }


    } );


  }

  setTab( tab: string ): void {
    this.activeTab = tab;
  }

  moveToShortlist( applicant: IApplication ): void {
    // this.shortlist.push( applicant );
  }

  moveToInterview( applicant: IApplication ): void {
    this.interviews.push( applicant );
  }

  moveInterviewToDecision( applicantId: string ) {
    const index = this.interviews.findIndex( app => app.id === applicantId );
    if ( index !== -1 ) {
      const applicant = this.interviews.splice( index, 1 )[0];
      this.decisions.push( applicant );
    }
  }

  onAccepted( application: IApplication ) {
    const existingApp = this.getAllApplications().find( app => app.id === application.id );
    if ( !existingApp ) return;

    this.removeFromAllLists( existingApp.id );
    existingApp.status = ApplicationStatus.ACCEPTED;
    this.hired.push( existingApp );
    this.showPopupService.showPopup( {
      status: 'isSuccess',
      translationKey: 'job_details.popup.application.accepted',
    } );
    this.cdr.detectChanges();
  }

  onOffered( application: IApplication ) {
    const existingApp = this.getAllApplications().find( app => app.id === application.id );
    if ( !existingApp ) return;

    this.removeFromAllLists( existingApp.id );
    existingApp.status = ApplicationStatus.OFFERED;
    this.offered.push( existingApp );
    //const title = this.translate.instant( 'popup.application.hired.title' );
    //const message = this.translate.instant( 'popup.application.hired.message' );
    this.showPopupService.showPopup( {
      status: 'isSuccess',
      translationKey: 'job_details.popup.application.hired',
    } );
    this.cdr.detectChanges();
  }

  onRejected( application: IApplication ) {
    const existingApp = this.getAllApplications().find( app => app.id === application.id );
    if ( !existingApp ) return;

    this.removeFromAllLists( existingApp.id );
    existingApp.status = ApplicationStatus.REJECTED;
    this.rejected.push( existingApp );
    this.showPopupService.showPopup( {
      status: 'failure',
      translationKey: 'job_details.popup.application.rejected',
    } );
    this.cdr.detectChanges();
  }

  private getAllApplications(): IApplication[] {
    return [
      ...this.applications,
      ...this.shortlist,
      ...this.interviews,
      ...this.decisions,
      ...this.rejected,
      ...this.offered,
      ...this.hired,
    ];
  }

  private removeFromAllLists( applicationId: string ): void {
    const allLists = [
      this.applications,
      this.shortlist,
      this.interviews,
      this.decisions,
      this.rejected,
      this.offered,
      this.hired,
    ];

    for ( const list of allLists ) {
      const index = list.findIndex( app => app.id === applicationId );
      if ( index !== -1 ) list.splice( index, 1 );
    }
  }

}
